import React from "react";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useReportRows from "../hooks/useReportRows";
import { UpdateReportRowPayload } from "../api/models/reportRow.models";
import { ReportRow } from "../api/models/report.models";
import { DialogInputs } from "../hooks/useMaterials";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { getDefaultValuesFromReportRow, isInteger } from "../utils/rowUtils";
import { SupClass } from "../api/models/supClass.models";
import AddMaterialInputs from "../components/addMaterialInputs";
import SupSelection from "../components/supSelection";
import GoBackLink from "../components/goBackLink";
import { Checkbox } from "primereact/checkbox";

const StyledView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  & h2 {
    font-size: 24px;
    padding: 2rem;
    text-align: left;
    font-weight: 600;
  }

  & form {
    width: 100%;

    & > div:first-child {
      padding: 0 2rem;
    }
  }

  .form-container {
    min-height: calc(100vh - 406px);
    width: 100%;
    max-width: 850px;
    text-align: left;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  gap: 0.25rem;

  & .p-inputtext,
  & .p-inputnumber {
    width: 100%;
  }
`;

const StickyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 100;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 4rem 1rem 2rem;
`;

interface EditReportRowView {
  show: boolean;
  onHide: () => void;
  reportRow: ReportRow;
  ean: string;
  reportId: number;
  ecomodClasses: EcomodClass[];
  supClasses?: SupClass[];
}

const EditReportRowDialog: React.FC<EditReportRowView> = ({
  show,
  onHide,
  reportRow,
  ean,
  reportId,
  ecomodClasses,
  supClasses,
}) => {
  const { t } = useTranslation();
  const { updateReportRowData, isUpdatingReportRow } = useReportRows();
  const [reportRowValues, setReportRowValues] = React.useState<
    RowValuesWithEcomodClass[]
  >([]);
  const [isSup, setIsSup] = React.useState(false);
  const [supClassCode, setSupClassCode] = React.useState<string | undefined>(
    undefined,
  );

  const materialDefaultValues = React.useMemo(() => {
    return getDefaultValuesFromReportRow(
      reportRow.values,
      ecomodClasses || [],
      supClasses || [],
    );
  }, [reportRow, ecomodClasses, supClasses]);

  React.useEffect(() => {
    if (materialDefaultValues.length > 0) {
      setIsSup(!!materialDefaultValues[0].isSup);
      setSupClassCode(materialDefaultValues[0].supClass);
    }
  }, [materialDefaultValues]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
  } = useForm<DialogInputs>({
    defaultValues: {
      reportId: reportId,
      name: reportRow.name,
      ean: ean || "",
      values: reportRow.values,
      responsibleToReport: reportRow.active,
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    reset({
      reportId: reportId,
      name: reportRow.name,
      ean: ean || "",
      values: reportRow.values,
      responsibleToReport: reportRow.active,
    });
    setReportRowValues(reportRow.values);
  }, [reportRow, reset]);

  React.useEffect(() => {
    if (reportRowValues.length > 0) {
      setValue("values", reportRowValues);
    }
  }, [reportRowValues, setValue]);

  const getFormErrorMessage = (name: string) => {
    return errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : (
      <></>
    );
  };

  const onSubmit = async (data: DialogInputs) => {
    const payload: UpdateReportRowPayload = {
      id: reportRow.id,
      name: data.name,
      ean: ean,
      amount: reportRow.amount,
      active: data.responsibleToReport,
      reportId,
      values: reportRowValues.map((value) => ({
        ...value,
        isEmptyCup: value.isEmptyCup !== undefined ? value.isEmptyCup : false,
      })),
    };

    await updateReportRowData(payload);
    onHide();
  };

  const saveDisabled = () => {
    return (
      reportRowValues?.length === 0 ||
      reportRowValues.some((value) => !value.ecoModulationClass) ||
      reportRowValues.some((value) => value.businessUse === undefined) ||
      reportRowValues.some((value) => value.value === 0) ||
      !getValues("name") ||
      reportRowValues.some(
        (value) =>
          !!value.secMaterial &&
          value.secValueType !== 2 &&
          (value.secValue ?? 0) > (value.value ?? 0),
      )
    );
  };

  return (
    <StyledView>
      <GoBackLink
        onClick={() => {
          onHide();
        }}
      >
        {`${t("go-back")}` || "← Go back"}
      </GoBackLink>

      <h2>{t("header-edit-report-row")}</h2>
      <form>
        <div className="form-container">
          {reportRow.ean !== "" && (
            <InputContainer>
              <label htmlFor="ean">{t("label-ean")}</label>
              <Controller
                name="ean"
                control={control}
                rules={{
                  required:
                    reportRow.ean === "" ? undefined : t("label-required"),
                }}
                render={({ field }) => (
                  <InputText id="ean" value={ean} disabled={true} />
                )}
              />
              {getFormErrorMessage("ean")}
            </InputContainer>
          )}

          <InputContainer>
            <Controller
              name="responsibleToReport"
              control={control}
              render={({ field }) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Checkbox
                    inputId="responsibleToReport"
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.checked);
                      if (!e.checked) {
                        setValue("comment", "");
                      }
                    }}
                  />
                  <label htmlFor="responsibleToReport">
                    {t("label-responsible-to-report")}
                  </label>
                </div>
              )}
            />
          </InputContainer>

          <br />

          <SupSelection
            isSup={isSup}
            onIsSupChange={setIsSup}
            supClass={supClassCode}
            onSupClassChange={setSupClassCode}
            supClasses={supClasses}
          />

          <AddMaterialInputs
            setReportRowValues={setReportRowValues}
            ecomodClasses={ecomodClasses}
            defaultValues={getDefaultValuesFromReportRow(
              reportRow.values,
              ecomodClasses || [],
              supClasses || [],
            )}
            supClasses={supClasses}
            isSup={isSup}
            supClass={supClassCode}
          />

          <br />
        </div>
        <StickyActions>
          <Button
            type="button"
            label={t("cmd-cancel")}
            className="p-button-text"
            onClick={onHide}
            disabled={isUpdatingReportRow}
          />
          <Button
            id="save-button"
            type="button"
            onClick={handleSubmit(onSubmit)}
            label={t("cmd-save")}
            className="p-ml-2"
            disabled={
              isUpdatingReportRow ||
              Object.keys(errors).length !== 0 ||
              saveDisabled()
            }
          />
        </StickyActions>
      </form>
    </StyledView>
  );
};

export default EditReportRowDialog;
